import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';

const WarrantyCoveragePage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [error, setError] = useState('');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !searchTerm.trim()) return;

    setLoading(true);
    setError('');
    setResults([]);

    try {
      // Search by serial number or agreement number
      const { data: equipmentData } = await supabase
        .from('equipment')
        .select(`
          *,
          manufacturer:manufacturers(*),
          property:properties(*),
          agreements:warranty_agreements(*,
            product:warranty_products(*),
            homeowner:people(*),
            contractor:contractors(*)
          )
        `)
        .ilike('serial_number', `%${searchTerm}%`);

      // Also search by agreement number
      const { data: agreementData } = await supabase
        .from('warranty_agreements')
        .select(`
          *,
          product:warranty_products(*),
          equipment:equipment(*,
            manufacturer:manufacturers(*),
            property:properties(*)
          ),
          homeowner:people(*),
          contractor:contractors(*)
        `)
        .ilike('agreement_number', `%${searchTerm}%`);

      const combinedResults: any[] = [];

      // Process equipment results
      if (equipmentData) {
        equipmentData.forEach(equipment => {
          if (equipment.agreements && equipment.agreements.length > 0) {
            equipment.agreements.forEach((agreement: any) => {
              combinedResults.push({
                type: 'equipment_search',
                equipment,
                agreement,
                status: agreement.status,
                coverage: getCoverageStatus(agreement)
              });
            });
          } else {
            combinedResults.push({
              type: 'equipment_no_warranty',
              equipment,
              agreement: null,
              status: 'no_warranty',
              coverage: { status: 'not_covered', message: 'No warranty agreement found' }
            });
          }
        });
      }

      // Process agreement results
      if (agreementData) {
        agreementData.forEach(agreement => {
          // Avoid duplicates from equipment search
          const existingResult = combinedResults.find(r =>
            r.agreement && r.agreement.id === agreement.id
          );

          if (!existingResult) {
            combinedResults.push({
              type: 'agreement_search',
              equipment: agreement.equipment,
              agreement,
              status: agreement.status,
              coverage: getCoverageStatus(agreement)
            });
          }
        });
      }

      setResults(combinedResults);

      if (combinedResults.length === 0) {
        setError('No warranty coverage found for this search term.');
      }

    } catch (error) {
      console.error('Error searching coverage:', error);
      setError('Error searching coverage. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCoverageStatus = (agreement: any) => {
    if (!agreement) {
      return { status: 'not_covered', message: 'No warranty agreement' };
    }

    const now = new Date();
    const startDate = new Date(agreement.start_date);
    const endDate = new Date(agreement.end_date);

    if (agreement.status !== 'active') {
      return {
        status: 'inactive',
        message: `Agreement is ${agreement.status}`,
        details: agreement.status === 'pending_delivery'
          ? 'Contract pending delivery to homeowner'
          : `Agreement ${agreement.status}`
      };
    }

    if (now < startDate) {
      return {
        status: 'future',
        message: 'Coverage starts in the future',
        details: `Coverage begins ${startDate.toLocaleDateString()}`
      };
    }

    if (now > endDate) {
      return {
        status: 'expired',
        message: 'Coverage has expired',
        details: `Coverage ended ${endDate.toLocaleDateString()}`
      };
    }

    // Calculate remaining coverage
    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsed = now.getTime() - startDate.getTime();
    const remaining = endDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(remaining / (1000 * 60 * 60 * 24));

    return {
      status: 'active',
      message: 'Active coverage',
      details: `${daysRemaining} days remaining until ${endDate.toLocaleDateString()}`,
      daysRemaining,
      percentComplete: Math.round((elapsed / totalDuration) * 100)
    };
  };

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-100">🔍 Warranty Coverage Verification</h1>
        <p className="text-slate-400 mt-1">Verify warranty coverage by serial number or agreement number</p>
      </div>

      {/* Search Form */}
      <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
        <form onSubmit={handleSearch} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Search by Serial Number or Agreement Number
            </label>
            <div className="flex gap-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Enter serial number (e.g., TCN240A1000A1) or agreement number (e.g., WA-2024-001000)"
                className="flex-1 p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                required
              />
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold transition-colors"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Searching...
                  </div>
                ) : (
                  '🔍 Search Coverage'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/20 border border-red-700 p-4 rounded-lg">
          <div className="text-red-100">{error}</div>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-100">Search Results ({results.length})</h2>

          {results.map((result, index) => (
            <CoverageResultCard key={index} result={result} />
          ))}
        </div>
      )}
    </div>
  );
};

const CoverageResultCard = ({ result }: { result: any }) => {
  const { equipment, agreement, coverage } = result;

  const statusColors = {
    active: 'bg-green-900 text-green-400 border-green-700',
    inactive: 'bg-yellow-900 text-yellow-400 border-yellow-700',
    expired: 'bg-red-900 text-red-400 border-red-700',
    future: 'bg-blue-900 text-blue-400 border-blue-700',
    not_covered: 'bg-slate-700 text-slate-400 border-slate-600'
  };

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  return (
    <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-100">
            {equipment?.manufacturer?.name} {equipment?.model_number}
          </h3>
          <p className="text-slate-400">Serial: {equipment?.serial_number}</p>
          {equipment?.property && (
            <p className="text-sm text-slate-400">
              Location: {equipment.property.street}, {equipment.property.city}, {equipment.property.state} {equipment.property.zip}
            </p>
          )}
        </div>

        <div className={`px-3 py-2 rounded-lg border ${statusColors[coverage.status as keyof typeof statusColors]}`}>
          <div className="font-semibold">{coverage.message}</div>
          {coverage.details && <div className="text-sm">{coverage.details}</div>}
        </div>
      </div>

      {agreement ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <div>
            <div className="text-sm text-slate-400">Agreement Number</div>
            <div className="font-mono text-blue-400">{agreement.agreement_number || 'Pending'}</div>
          </div>
          <div>
            <div className="text-sm text-slate-400">Product</div>
            <div className="text-slate-300">{agreement.product?.name}</div>
          </div>
          <div>
            <div className="text-sm text-slate-400">Coverage Period</div>
            <div className="text-slate-300">
              {new Date(agreement.start_date).toLocaleDateString()} - {new Date(agreement.end_date).toLocaleDateString()}
            </div>
          </div>
          <div>
            <div className="text-sm text-slate-400">Premium</div>
            <div className="text-slate-300">{formatCurrency(agreement.premium_cents)}</div>
          </div>

          {coverage.status === 'active' && (
            <>
              <div>
                <div className="text-sm text-slate-400">Deductible</div>
                <div className="text-slate-300">{formatCurrency(agreement.deductible_cents)}</div>
              </div>
              <div>
                <div className="text-sm text-slate-400">Per Repair Limit</div>
                <div className="text-slate-300">{formatCurrency(agreement.per_repair_cap_cents)}</div>
              </div>
              <div>
                <div className="text-sm text-slate-400">Aggregate Limit</div>
                <div className="text-slate-300">{formatCurrency(agreement.aggregate_cap_cents)}</div>
              </div>
              <div>
                <div className="text-sm text-slate-400">Labor Rate</div>
                <div className="text-slate-300">{formatCurrency(agreement.labor_rate_band)}/hour</div>
              </div>
            </>
          )}

          {agreement.homeowner && (
            <div>
              <div className="text-sm text-slate-400">Homeowner</div>
              <div className="text-slate-300">
                {agreement.homeowner.first_name} {agreement.homeowner.last_name}
              </div>
            </div>
          )}

          {agreement.contractor && (
            <div>
              <div className="text-sm text-slate-400">Installing Contractor</div>
              <div className="text-slate-300">{agreement.contractor.legal_name}</div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4 p-4 bg-slate-800 rounded-lg">
          <div className="text-slate-300">
            <strong>No Warranty Coverage Found</strong>
          </div>
          <div className="text-sm text-slate-400 mt-1">
            This equipment does not have an active warranty agreement. Contact your HVAC contractor to purchase warranty coverage.
          </div>
        </div>
      )}

      {coverage.status === 'active' && coverage.percentComplete && (
        <div className="mt-4">
          <div className="flex justify-between text-sm text-slate-400 mb-1">
            <span>Coverage Progress</span>
            <span>{coverage.percentComplete}% complete</span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${coverage.percentComplete}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarrantyCoveragePage;